/** 资源画布共用九列栅格，坐标均从0开始。 */
export const MODULE_COLUMNS = 9;
export const MODULE_MAX_ROW = 2;

export interface ModuleResource {
    id: number;
    name: string;
    des: string;
    iconUrl: string;
    iconBack: string;
    orderIndex: number;
    pageType: number;
    layoutX: number | null;
    layoutY: number | null;
    layoutW: number;
    layoutH: number;
    iconSize: number;
}

export interface ModuleTile extends ModuleResource {
    layoutX: number;
    layoutY: number;
}

export interface LayoutResult {
    valid: boolean;
    items: ModuleTile[];
    reason: string;
    exchanged: number[];
}

/** 判断两个栅格矩形是否重叠，边缘相邻不算重叠。 */
export function overlaps(a: ModuleTile, b: ModuleTile): boolean {
    return a.layoutX < b.layoutX + b.layoutW && a.layoutX + a.layoutW > b.layoutX
        && a.layoutY < b.layoutY + b.layoutH && a.layoutY + a.layoutH > b.layoutY;
}

/** 检查矩形尺寸及九列边界，拒绝小数和非法数值。 */
export function inBounds(tile: ModuleTile): boolean {
    return [tile.layoutX, tile.layoutY, tile.layoutW, tile.layoutH].every(Number.isInteger)
        && tile.layoutX >= 0 && tile.layoutY >= 0 && tile.layoutY <= MODULE_MAX_ROW
        && tile.layoutW >= 1 && tile.layoutW <= MODULE_COLUMNS
        && tile.layoutH >= 1 && tile.layoutH <= 3
        && tile.layoutX + tile.layoutW <= MODULE_COLUMNS
        && tile.layoutY + tile.layoutH <= 3;
}

/** 三行之外、未配置或相互重叠的资源保留在未放置区，不自动增加第四行。 */
export function normalizeResources(rows: Partial<ModuleResource>[]): ModuleResource[] {
    const placed: ModuleTile[] = [];
    return [...rows].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0) || Number(a.id) - Number(b.id)).map(row => {
        const item = {...row, layoutW: Math.max(1, Math.min(9, row.layoutW || 1)),
            layoutH: Math.max(1, Math.min(3, row.layoutH || 1)), iconSize: row.iconSize || 48} as ModuleResource;
        if (item.layoutX != null && item.layoutY != null && inBounds(item as ModuleTile)
            && !placed.some(other => overlaps(item as ModuleTile, other))) {
            placed.push(item as ModuleTile);
            return item;
        }
        return {...item, layoutX: null, layoutY: null};
    });
}

/** 判断资源是否已配置在主画布中。 */
export function isPlaced(item: ModuleResource): item is ModuleTile {
    return item.layoutX != null && item.layoutY != null;
}

/** 校验整个布局，供交换、缩放及表单保存共同使用。 */
export function validLayout(items: ModuleTile[]): boolean {
    return new Set(items.map(item => item.id)).size === items.length
        && items.every((item, index) => inBounds(item)
            && !items.slice(index + 1).some(other => overlaps(item, other)));
}

/** 为未配置或相互冲突的模块寻找空位，优先保留所有已有效配置的位置。 */
export function normalizeModules(rows: Partial<ModuleResource>[]): ModuleTile[] {
    const accepted: ModuleTile[] = [];
    const pending: ModuleTile[] = [];
    const ordered = [...rows].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0) || Number(a.id) - Number(b.id));
    ordered.forEach(row => {
        const tile = { ...row, layoutW: row.layoutW || 1, layoutH: row.layoutH || 1,
            iconSize: row.iconSize || 48 } as ModuleTile;
        if (row.layoutX != null && row.layoutY != null && inBounds(tile) && !accepted.some(other => overlaps(tile, other))) {
            accepted.push(tile);
        } else {
            pending.push(tile);
        }
    });
    pending.forEach(tile => {
        tile.layoutW = Math.max(1, Math.min(MODULE_COLUMNS, Math.trunc(tile.layoutW)));
        tile.layoutH = Math.max(1, Math.min(3, Math.trunc(tile.layoutH)));
        let placed = false;
        for (let y = 0; y <= MODULE_MAX_ROW && !placed; y++) {
            for (let x = 0; x <= MODULE_COLUMNS - tile.layoutW; x++) {
                const candidate = { ...tile, layoutX: x, layoutY: y };
                if (inBounds(candidate) && !accepted.some(other => overlaps(candidate, other))) {
                    accepted.push(candidate);
                    placed = true;
                    break;
                }
            }
        }
        if (!placed) throw new Error('资源过多，画布已没有可用位置');
    });
    return ordered.map(row => accepted.find(tile => tile.id === row.id) as ModuleTile);
}

/** 拖至空位直接移动；完整覆盖等面积资源时整体换位，保持被换资源的相对位置。 */
export function moveModule(items: ModuleTile[], id: number, x: number, y: number): LayoutResult {
    const source = items.find(item => item.id === id);
    if (!source) return { valid: false, items, reason: '资源不存在', exchanged: [] };
    const target = { ...source, layoutX: x, layoutY: y };
    if (!inBounds(target)) return { valid: false, items, reason: '按钮不能超出画布边界', exchanged: [] };
    const hits = items.filter(item => item.id !== id && overlaps(target, item));
    if (hits.length) {
        const fullyCovered = hits.every(item => item.layoutX >= x && item.layoutY >= y
            && item.layoutX + item.layoutW <= x + source.layoutW
            && item.layoutY + item.layoutH <= y + source.layoutH);
        const equalArea = hits.reduce((sum, item) => sum + item.layoutW * item.layoutH, 0) === source.layoutW * source.layoutH;
        if (!fullyCovered || !equalArea) {
            return { valid: false, items, reason: '只能交换同尺寸按钮，或完整覆盖同面积的一组按钮', exchanged: [] };
        }
    }
    const moved = items.map(item => {
        if (item.id === id) return target;
        if (!hits.some(hit => hit.id === item.id)) return { ...item };
        return { ...item, layoutX: item.layoutX + source.layoutX - x, layoutY: item.layoutY + source.layoutY - y };
    });
    if (!validLayout(moved)) return { valid: false, items, reason: '交换后的空间不足，请完整对齐目标区域', exchanged: [] };
    return { valid: true, items: moved, reason: '', exchanged: hits.map(item => item.id) };
}

/** 保持左上角不变调整宽高，扩展区域被其它模块占用时拒绝覆盖。 */
export function resizeModule(items: ModuleTile[], id: number, w: number, h: number): LayoutResult {
    const next = items.map(item => item.id === id ? { ...item, layoutW: w, layoutH: h } : { ...item });
    if (!next.some(item => item.id === id) || !validLayout(next)) {
        return { valid: false, items, reason: '尺寸超出边界或占用了其它按钮，请先移开周围按钮', exchanged: [] };
    }
    return { valid: true, items: next, reason: '', exchanged: [] };
}

/** 提取布局字段，避免保存布局时覆盖模块名称、URL、图标路径及权限相关资料。 */
export function layoutPayload(items: Partial<ModuleResource>[]) {
    return items.map(item => ({ id: item.id, layoutX: item.layoutX == null ? null : item.layoutX,
        layoutY: item.layoutY == null ? null : item.layoutY, layoutW: item.layoutW || 1,
        layoutH: item.layoutH || 1, iconSize: item.iconSize || 48 })).sort((a, b) => Number(a.id) - Number(b.id));
}

/** 生成稳定快照，用于脏数据检查、撤销及保存时的原始值比对。 */
export function layoutSignature(items: Partial<ModuleResource>[]): string {
    return JSON.stringify(layoutPayload(items));
}
